import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { API_ENDPOINTS } from '../../config/api';

export interface AssetValueRange {
    id: string;
    label: string;
    minAge: number;
    maxAge: number;
    price: number;
}

export interface AssetValuesState {
    list: AssetValueRange[];
    loading: boolean;
    error: string | null;
    updateLoading: boolean;
}

const initialState: AssetValuesState = {
    list: [],
    loading: false,
    error: null,
    updateLoading: false,
};

export const fetchAssetValues = createAsyncThunk(
    'assetValues/fetchAssetValues',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(API_ENDPOINTS.getAssetValues());
            return response.data; // Expecting { data: [...] }
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.detail || 'Failed to fetch asset values');
        }
    }
);

export const updateAssetValue = createAsyncThunk(
    'assetValues/updateAssetValue',
    async ({ adminMobile, ranges }: { adminMobile: string; ranges: { id: string; price: number }[] }, { dispatch, rejectWithValue }) => {
        try {
            await api.put(API_ENDPOINTS.updateAssetValues(), { ranges }, {
                headers: { 'x-super-admin-mobile': adminMobile }
            });
            dispatch(fetchAssetValues());
            return true;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.detail || 'Failed to update asset values');
        }
    }
);

const assetValuesSlice = createSlice({
    name: 'assetValues',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchAssetValues.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchAssetValues.fulfilled, (state, action) => {
            state.loading = false;
            state.list = action.payload.data || [];
        });
        builder.addCase(fetchAssetValues.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
        builder.addCase(updateAssetValue.pending, (state) => {
            state.updateLoading = true;
        });
        builder.addCase(updateAssetValue.fulfilled, (state) => {
            state.updateLoading = false;
        });
        builder.addCase(updateAssetValue.rejected, (state) => {
            state.updateLoading = false;
        });
    }
});

export const assetValuesReducer = assetValuesSlice.reducer;
export default assetValuesSlice.reducer;
